import { fetch } from "whatwg-fetch";
import { AllIconNames } from "react-ui";

import {
    ensureHasFunctionType,
    ensureNotNullOrUndefined,
    ErrorFirstCallback,
    reject,
} from "../../../../Common/TypingUtils";
import type { IForm } from "../../Engine/Form/IForm";
import { AsyncLoadIntoDocument } from "../../Helpers/AsyncLoadIntoDocument";
import { buildUrl, buildPublicScriptUrl, buildPublicStyleUrl, RequestParams } from "../../Helpers/Url";
import type { IPublicForm } from "../PublicForm/IPublicForm";
import type { IKeWebTemplatesApiClient } from "../Types/IKeWebTemplatesApiClient";
import type { ClientDraftInfoResponse } from "../../../../Common/CandyApiClient/ClientDraftInfo/ClientDraftInfoResponse";
import { getClientDraftInfoUrl } from "../../../../Common/CandyApiClient/ClientDraftInfo/GetClientDraftInfoUrl";
import { AUOOptions } from "../Types/AUOOptions";
import type { LayoutEventsStatic } from "../../Controls/Internals/LayoutEvents/LayoutEvents";
import { AbsoluteResolvedModelPath } from "../../../../Common/ModelPath/ModelPath";
import { isNetworkError, HttpCode } from "../../Helpers/NetworkError";

import { clientSettings } from "./ClientSettings";

export interface ExtraPageActionAttachment {
    tid?: string;
    description: string;
    handler: (instancePath?: string) => void;
    disabled: boolean;
}

type FormContainer = string | HTMLElement;

type EngineEntryPoint = () => void;

export interface ActionDescription {
    icon: AllIconNames;
    text?: string | (() => string);
    onClick: () => void;
    hint?: undefined | string;
    id: string;
    tid?: string;
    weight: number;
}

interface RenderFormSettings {
    gfv: string;
    engineVersion?: undefined | null | string;
    theme?: undefined | null | string;
    resourceZone?: string;
    renderForm: undefined | EngineEntryPoint;
    templatesApi?: IKeWebTemplatesApiClient;
    panelActions?: ActionDescription[];

    /**
     * Используется для тестирования (на момент добавления в Диадоке)
     */
    passBindingPathsIntoDomElements?: boolean;
}

type StartFormFunc = (pageContent: FormContainer, extendedSettings: RenderFormSettings) => Promise<IForm>;

interface CandyGlobalInjectedVar {
    start: (pageContent: FormContainer, options: StartFormOptions, callback?: ErrorFirstCallback<IPublicForm>) => void;
    keformsUrl?: string;
    staffServiceUrl: string;
    contractorsApiUrl: string;
    picklistUrl: string;
    nidusUrl: string;
    fsPrinterUrl: string;
    partialRenderingForIE8: boolean;
    entryPoint: { [key: string]: StartFormFunc };
    loadError?: unknown;
}

type Loader = (params: RequestParams) => Promise<void>;
declare global {
    interface Window extends Record<string, string | Loader> {
        CF: CandyGlobalInjectedVar;
    }

    namespace NodeJS {
        interface Global {
            CF: CandyGlobalInjectedVar;
        }
    }
}

declare const global: Window;

function defaultCallback(): void {
    /* empty */
}

function parseBool(value: unknown): boolean {
    if (typeof value === "string") {
        if (value === "false") {
            return false;
        }
        if (value === "true") {
            return true;
        }
        return false;
    }
    return Boolean(value);
}

function isAllowed(domains: string): boolean {
    return new RegExp(`^(?:${domains.replace(/\./g, "\\.").replace(/\*/g, "[a-z0-9-._]+")})$`).test(
        global.location.host
    );
}

function getParameterByName(name: string): string | undefined {
    return (global.location.search
        .trim()
        .replace(/^(\?|#|&)/, "")
        .split("&")
        .map(param => param.replace(/\+/g, " ").split("=").map(decodeURIComponent))
        .find(([key]) => key == name) || [])[1];
}

interface StartFormSetting {
    gfv: string;
    engineVersion?: undefined | null | string;
    resourceZone?: string;
    theme?: undefined | null | string;
}

async function loadForm(settings: StartFormSetting): Promise<void> {
    const { gfv, engineVersion } = settings;

    if (global.CF.entryPoint[gfv]) {
        return;
    }

    const tasks: Array<Promise<void>> = [];
    const min = getParameterByName("debug") === "true" ? "" : ".min";
    const params: RequestParams = { resourceZone: settings.resourceZone };

    tasks.push(AsyncLoadIntoDocument.styleSheet(buildPublicStyleUrl(`${gfv}${min}.css`, params), true));
    tasks.push(AsyncLoadIntoDocument.script(buildPublicScriptUrl(`${gfv}${min}.js`, params)));

    const isNeedEngine = engineVersion && !global[engineVersion];
    if (isNeedEngine) {
        tasks.push(loadEngine(engineVersion, min, params));
    }

    await Promise.all(tasks);

    if (!global.CF.entryPoint[gfv]) {
        throw new Error(`Entry point is absent for gfv: ${gfv}`);
    }
}

async function loadEngine(version: string, min: string, params: RequestParams): Promise<void> {
    try {
        await AsyncLoadIntoDocument.script(buildPublicScriptUrl(`engineLoader_${version}${min}.js`, params));
    } catch (error) {
        if (isNetworkError(error) && error.status === HttpCode.NotFound) {
            // Пытаемся загрузить старую версию движка (до внедрения engineLoader)
            await Promise.all([
                AsyncLoadIntoDocument.script(buildPublicScriptUrl(`candy_${version}${min}.js`, params)),
                AsyncLoadIntoDocument.styleSheet(buildPublicStyleUrl(`candy_${version}${min}.css`, params), true),
            ]);
            return;
        }

        throw error;
    }

    const engineLoaderName = `loadEngine_${version}`;
    const engineLoader = global[engineLoaderName];
    ensureNotNullOrUndefined(engineLoader, `window.${engineLoaderName} must be defined`);
    ensureHasFunctionType(engineLoader, `window.${engineLoaderName} must be a function`);

    await engineLoader(params);
}

function renderForm(
    pageContent: FormContainer,
    extendedSettings: RenderFormSettings,
    callback: ErrorFirstCallback<IPublicForm>
): void {
    const formEntryPoint =
        global.CF.entryPoint[extendedSettings.gfv] ?? reject(`Form entry point ${extendedSettings.gfv} not found.`);
    formEntryPoint(pageContent, extendedSettings)
        .then(form => {
            const publicForm = form.publicForm;

            callback(null, publicForm);
        })
        .catch(callback);
}

export interface StartFormOptions extends Partial<AUOOptions> {
    ns: string;
    draftId: string;
    gfv?: string;
    resourceZone?: string;
    templatesApi?: IKeWebTemplatesApiClient;
    feedbackEnabled?: boolean;
    isDisabledCloseButton?: boolean;
    isDisabledCloseAndSaveButton?: boolean;
    isDisabledCleanForm?: boolean;
    hidePanelActions?: boolean;
    theme?: undefined | null | string;
    authentication?: "browser-credentials-include" | { authSid: string };
    fetch?: typeof fetch;
    model?: { [path: string]: unknown };
    panelActions?: ActionDescription[];
    eventEmitter?: LayoutEventsStatic;
    employerId?: string;
    showNavigationSidebar?: boolean;
    onRemoveAttachment?: () => void;
    onClearAttachment?: () => void;
    attachmentId?: string;
    attachmentPageActions?: ExtraPageActionAttachment[];
    setUnitItemExtraInfo?: (info?: Array<string | undefined>) => void;
    attachmentUnitItemInfoPaths?: AbsoluteResolvedModelPath | AbsoluteResolvedModelPath[];
    hideFormLoader?: boolean;
}

function getEngineEntryPoint(engineVersion: undefined | null | string): undefined | EngineEntryPoint {
    if (engineVersion == undefined) {
        return undefined;
    }
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return global[engineVersion] && global[engineVersion].default;
}

const start: CandyGlobalInjectedVar["start"] = (pageContent, options, callback = defaultCallback) => {
    let error = null;
    const { ns, draftId } = options;

    if (!isAllowed(clientSettings.konturDomains)) {
        error = new Error("domain.untrusted");
    } else if (!clientSettings.authorized) {
        error = new Error("user.unauthenticated");
    } else if (!draftId) {
        error = new Error("options.draftId is required");
    } else if (!ns) {
        error = new Error("options.ns is required");
    }

    if (error) {
        callback(error);
        return;
    }

    const resourceZone = options.resourceZone ?? getParameterByName("resourceZone");
    const clientDraftUrl = buildUrl(getClientDraftInfoUrl({ ns: ns, draftId: draftId }), {
        resourceZone: resourceZone,
    });

    const requestOptions: RequestInit = { method: "GET" };
    if (options.authentication == undefined || options.authentication === "browser-credentials-include") {
        requestOptions.credentials = "include";
    } else if ("authSid" in options.authentication && options.authentication.authSid != undefined) {
        requestOptions.credentials = "omit";
        requestOptions.headers = requestOptions.headers ?? {};
        // @ts-ignore
        requestOptions.headers["Authorization"] = `AUTH.SID ${options.authentication.authSid}`;
    } else {
        requestOptions.credentials = "omit";
    }
    const fetchImpl = options.fetch ?? fetch;

    fetchImpl(clientDraftUrl, requestOptions)
        .then(x => x.json())
        .then((clientDraftInfo: ClientDraftInfoResponse) => {
            const gfv = clientDraftInfo.gfv;
            const engineVersion = clientDraftInfo.engineVersion;
            const extendedSettings = {
                authorized: clientSettings.authorized,
                cauldronApiUrl: clientSettings.cauldronApiUrl,
                isTestServer: clientSettings.isTestServer,
                renderCachingForIE8: clientSettings.renderCachingForIE8,
                normalizationOnServer: clientSettings.normalizationOnServer,
                normativeUrl: clientSettings.normativeUrl,
                ndsPlusApiUrl: clientSettings.ndsPlusApiUrl,
                konturDomains: clientSettings.konturDomains,
                showNavigationSidebar: options.showNavigationSidebar ?? true,
                onRemoveAttachment: options.onRemoveAttachment,
                onClearAttachment: options.onClearAttachment,
                ...options,
                ...clientDraftInfo,
                gfv: gfv,
                engineVersion: engineVersion,
                feedbackEnabled: options.feedbackEnabled ?? clientDraftInfo.feedbackEnabled,
                isDisabledCleanForm: options.isDisabledCleanForm ?? false,
                isDisabledCloseButton: options.isDisabledCloseButton ?? false,
                isDisabledCloseAndSaveButton: options.isDisabledCloseAndSaveButton ?? false,
                resourceZone: resourceZone,
                setUnitItemExtraInfo: options.setUnitItemExtraInfo,
                attachmentUnitItemInfoPath: options.attachmentUnitItemInfoPaths,
            };

            loadForm({
                gfv: extendedSettings.gfv,
                engineVersion: extendedSettings.engineVersion,
                resourceZone: extendedSettings.resourceZone,
                theme: extendedSettings.theme,
            })
                .then(() => {
                    const render = getEngineEntryPoint(engineVersion);

                    renderForm(
                        pageContent,
                        {
                            ...extendedSettings,
                            templatesApi: options.templatesApi,
                            renderForm: render,
                        },
                        callback
                    );
                })
                .catch(loadError => {
                    callback(new Error(`dynamic loading is not supported for gfv ${gfv}\n${loadError}`));
                });
        })
        .catch(callback);
};

const CandyClientExports: CandyGlobalInjectedVar = {
    start: start,
    entryPoint: {},
    partialRenderingForIE8: parseBool("{partialRenderingForIE8}"),
    keformsUrl: "{keformsUrl}",
    staffServiceUrl: "{staffServiceUrl}",
    picklistUrl: "{picklistApiUrl}",
    nidusUrl: "{nidusApiUrl}",
    fsPrinterUrl: "{fsprinterApiUrl}",
    contractorsApiUrl: "{contractorsApiUrl}",
};

global.CF = CandyClientExports;

export { CandyClientExports };
